import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import { RISK_BAND_COLORS } from '../constants';

import {BandChip} from '../../DashboardCommon/widgets.comp';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const TEAL   = '#1B6B55';

// Monitor/Watch/Elevated/Priority Concern match RISK_BAND_COLORS exactly;
// Critical/High/Medium/Low are this tab's own severity vocabulary, kept local.
/* ── SeverityChip (duplicated from RiskOverview) ────────────────── */
const SEVERITY = {
    ...Object.fromEntries(Object.entries(RISK_BAND_COLORS).map(([band, hex]) => [band, {bg: hex, color: '#ffffff'}])),
    Critical: {bg: '#B91C1C', color: '#ffffff'},
    High:     {bg: '#E04535', color: '#ffffff'},
    Medium:   {bg: '#EFA020', color: '#ffffff'},
    Low:      {bg: '#4A9278', color: '#ffffff'},
};

const SeverityChip = ({label}) => {
    const s = SEVERITY[label] || {bg: '#6B7280', color: '#ffffff'};
    return <BandChip label={label} color={s.bg} width={140}/>;
};

/* Left panel — bar color by severity */
const RISK_BAR_COLOR = {
    ...RISK_BAND_COLORS,
    Low:      '#4A9278',
    Medium:   '#EFA020',
    High:     '#E04535',
    Critical: '#B91C1C',
};

/* Right panel — percentage text color */
const RISK_PCT_COLOR = {
    ...RISK_BAND_COLORS,
    Critical: '#B91C1C',
    High:     '#E04535',
    Medium:   '#EFA020',
    Low:      '#4A9278',
};

const DeploymentPanel = ({title, rows}) => (
    <Box sx={{
        flex: 1,
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        p: 3,
    }}>
        <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 3}}>
            {title}
        </Typography>

        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.4}}>
            {rows.map((row, idx) => (
                <Box key={`${row.name}-${idx}`} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
                    <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY}}>
                        {row.name}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0}}>
                        {/* Colored percentage */}
                        {row.suppressed ? <SuppressedValue/> : (<>
                        <Typography sx={{
                            fontSize: '18px',
                            fontWeight: 800,
                            color: RISK_PCT_COLOR[row.band] || NAVY,
                        }}>
                            {row.pct === null ? '—' : `${row.pct}%`}
                        </Typography>
                        {/* Solid severity chip */}
                        {row.band && <SeverityChip label={row.band}/>}
                        </>)}
                    </Box>
                </Box>
            ))}
        </Box>
    </Box>
);

// Ranked by prevalence; units under the n floor (no figure) go to the back.
const byPctDesc = (a, b) => (b.pct ?? -1) - (a.pct ?? -1);

const cleanLabel = (label) => {
    if (label && label.includes('·')) {
        return label.split('·')[1].trim();
    }
    return label;
};

const CYBER_RISK_FLAGS_SECTION_CODE = 'S_15';
const CARD_SECTION = CYBER_RISK_FLAGS_SECTION_CODE;

const CyberRiskFlagsTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });

    const s15Section = useMemo(() => {
        return dashboardData?.cards?.sections?.find(
            (section) => section.section_code === CYBER_RISK_FLAGS_SECTION_CODE
        );
    }, [dashboardData]);

    const c2 = useMemo(() => {
        return s15Section?.cards?.find(c => c.card_code === 'C_2');
    }, [s15Section]);

    const c3 = useMemo(() => {
        return s15Section?.cards?.find(c => c.card_code === 'C_3');
    }, [s15Section]);

    const c4a = useMemo(() => {
        return s15Section?.cards?.find(c => c.card_code === 'C_4a');
    }, [s15Section]);

    const c4b = useMemo(() => {
        return s15Section?.cards?.find(c => c.card_code === 'C_4b');
    }, [s15Section]);

    // Data Map PD-18-01: High-Risk Countries is "Any Cyber Risk by country",
    // PD.S_15.C_5 (labels are plain country names). C_4a is not in the Data Map;
    // it stays only as a fallback for older dashboards.
    const c5 = useMemo(() => {
        return s15Section?.cards?.find(c => c.card_code === 'C_5');
    }, [s15Section]);

    const riskRows = useMemo(() => {
        const rawItems = c3?.items || [];
        // A unit under the n floor arrives flagged: no figure, no band (and no made-up "Medium").
        return rawItems.map(item => ({
            label: cleanLabel(item.label),
            suppressed: isSuppressedItem(item),
            pct: isSuppressedItem(item) || item.value == null ? null : item.value,
            band: isSuppressedItem(item) ? null : (item.band || null)
        }));
    }, [c3]);

    const toDeploymentRow = (item) => {
        let countryName = '';
        if (item.label && item.label.includes('·')) {
            countryName = item.label.split('·')[1].trim();
        }
        return {
            name: item.label,
            suppressed: isSuppressedItem(item),
            pct: isSuppressedItem(item) || item.value == null ? null : item.value,
            band: isSuppressedItem(item) ? null : (item.band || null),
            country: countryName
        };
    };

    const highRiskCountries = useMemo(() => {
        if (c5) {
            return (c5.items || [])
                .map((item) => ({...toDeploymentRow(item), country: item.label}))
                .sort(byPctDesc);
        }
        return (c4a?.items || []).map(toDeploymentRow).sort(byPctDesc);
    }, [c5, c4a]);

    const highRiskSchools = useMemo(() => {
        return (c4b?.items || []).map(toDeploymentRow).sort(byPctDesc);
    }, [c4b]);

    const recommendedActionText = useMemo(() => {
        const isCritical = d => d.band === 'Priority Concern' || d.band === 'Critical';
        // A deployment is a school or network (Yufei-PD-27Oct-01): a country is
        // where they are, not one more deployment — counted together, one school
        // in Germany read "Route the 2 critical deployments in Germany". The
        // countries named are those schools' own (from "School · Country"), not
        // every critical country, which named three countries for two schools.
        const criticalSchools = highRiskSchools.filter(isCritical);
        const count = criticalSchools.length;

        if (count > 0) {
            const countries = Array.from(new Set(
                criticalSchools.map(d => d.country).filter(Boolean)
            ));

            let countryStr = '';
            if (countries.length === 1) {
                countryStr = countries[0];
            } else if (countries.length === 2) {
                countryStr = `${countries[0]} and ${countries[1]}`;
            } else if (countries.length > 2) {
                countryStr = `${countries.slice(0, -1).join(', ')}, and ${countries[countries.length - 1]}`;
            }

            const depLabel = count === 1 ? 'deployment' : 'deployments';
            const countrySection = countryStr ? ` in ${countryStr}` : '';
            return `Route the ${count} critical ${depLabel}${countrySection} to safeguarding immediately, pair all high-risk schools with parent support, and never expose per-child risk data outside authorized safeguarding roles.`;
        }

        return 'Pair all high-risk schools with parent support, and never expose per-child risk data outside authorized safeguarding roles.';
    }, [highRiskSchools]);

    if (loading) {
        return <CircularLoader />;
    }

    if (!s15Section) {
        return (
            <NoDataAvailable />
        );
    }

    return (
        <Box>
            {/* ── Warning banner ── */}
            <Box sx={{
                mb: 3,
                p: {xs: 2, md: 2.5},
                backgroundColor: 'var(--brand-surface-subtle, #FEE2E2)',
                border: '1px solid #FECACA',
                borderLeft: '4px solid #DC2626',
                borderRadius: '0 8px 8px 0',
            }}>
                {c2?.leftline_html ? (
                    <Typography 
                        sx={{
                            fontSize: '13px', 
                            color: '#374151', 
                            lineHeight: 1.7,
                            '& p': { margin: 0 }
                        }}
                        dangerouslySetInnerHTML={{ __html: c2.leftline_html }}
                    />
                ) : (
                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                        <strong style={{color: '#DC2626'}}>Use with care.&nbsp;</strong>
                        Differences should prompt support, not assumptions about any school or country. Small groups
                        and sensitive risks require suppression or authorized safeguarding access.
                    </Typography>
                )}
            </Box>

            <Box sx={{display: 'flex', gap: 2.5, alignItems: 'stretch'}}>

                {/* ── Left: Most prevalent risks ── */}
                {/*<Box sx={{*/}
                {/*    flex: 1,*/}
                {/*    border: '1px solid var(--brand-border, #E5E7EB)',*/}
                {/*    borderRadius: '12px',*/}
                {/*    backgroundColor: 'var(--brand-surface, #ffffff)',*/}
                {/*    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',*/}
                {/*    p: 3,*/}
                {/*}}>*/}
                {/*    <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 3}}>*/}
                {/*        {c3?.title || 'Most prevalent risks across the portfolio'}*/}
                {/*    </Typography>*/}

                {/*    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.2}}>*/}
                {/*        {riskRows.map((row, idx) => (*/}
                {/*            <Box key={`${row.label}-${idx}`} sx={{display: 'flex', alignItems: 'center', gap: 2}}>*/}
                {/*                /!* Risk name *!/*/}
                {/*                <Typography sx={{*/}
                {/*                    fontSize: '14px', */}
                {/*                    color: '#374151', */}
                {/*                    width: '195px', */}
                {/*                    flexShrink: 0,*/}
                {/*                    whiteSpace: 'normal',*/}
                {/*                    lineHeight: 1.3*/}
                {/*                }}>*/}
                {/*                    {row.label}*/}
                {/*                </Typography>*/}
                {/*                /!* Bar *!/*/}
                {/*                <Box sx={{flex: 1, height: '22px', borderRadius: '4px', backgroundColor: 'var(--brand-surface-subtle, #E5E7EB)', overflow: 'hidden'}}>*/}
                {/*                    <Box sx={{*/}
                {/*                        width: `${row.pct}%`,*/}
                {/*                        height: '100%',*/}
                {/*                        backgroundColor: RISK_BAR_COLOR[row.band] || NAVY,*/}
                {/*                        borderRadius: '4px',*/}
                {/*                        transition: 'width 0.4s ease',*/}
                {/*                    }}/>*/}
                {/*                </Box>*/}
                {/*                /!* Percentage *!/*/}
                {/*                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, minWidth: '42px', textAlign: 'right', flexShrink: 0}}>*/}
                {/*                    {row.pct}%*/}
                {/*                </Typography>*/}
                {/*                /!* Solid severity chip — same shade as the bar, not a pastel tint *!/*/}
                {/*                <SeverityChip label={row.band}/>*/}
                {/*            </Box>*/}
                {/*        ))}*/}
                {/*    </Box>*/}
                {/*</Box>*/}

                {/* ── Middle: High-risk countries ── */}
                <DeploymentPanel title="High-Risk Countries" rows={highRiskCountries}/>

                {/* ── Right: High-risk schools ── */}
                <DeploymentPanel title="High-Risk Schools" rows={highRiskSchools}/>

            </Box>

            {/* ── Recommended action footer ── */}
            <Box sx={{
                mt: 3,
                p: {xs: 2, md: 2.5},
                backgroundColor: 'var(--brand-surface-subtle, #F0F7F4)',
                border: '1px solid #C6E0D8',
                borderLeft: `4px solid ${TEAL}`,
                borderRadius: '0 8px 8px 0',
            }}>
                <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                    <strong style={{color: TEAL}}>Recommended action:&nbsp;</strong>
                    {recommendedActionText}
                </Typography>
            </Box>
        </Box>
    );
};

export default CyberRiskFlagsTab;
